import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Box, Paper, Slide } from '@mui/material';
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import {
  notifyIncomingCallAtom,
  notifyClosePopUpAtom,
  visibilityStateAtom,
} from '../../../Atoms/HubAtoms';
import IncomingCallContent from './IncomingCallContent';
import {
  isNotificationSupported,
  notify,
} from '../../../Helpers/notifications';
import useAPI from '../../../api';
import {
  additionalCustomerDataAtom,
  selectedCustomerIdAtom,
  selectedBranchAtom,
  userDataAtom,
} from '../../../Atoms';

const IncomingCallPopup = function IncomingCallPopup() {
  const [calls, setCalls] = useState([]); //one entry per active incoming call
  const notifyIncomingCall = useAtomValue(notifyIncomingCallAtom);
  const visibilityState = useAtomValue(visibilityStateAtom);
  const [notifyClosePopUp, setNotifyClosePopUp] = useAtom(notifyClosePopUpAtom); //{ customerId, answeredByUserId } of the call to close
  const selectedBranch = useAtomValue(selectedBranchAtom);
  const userData = useAtomValue(userDataAtom);
  const setSelectedCustomerId = useSetAtom(selectedCustomerIdAtom);
  const setAdditionalCustomerData = useSetAtom(additionalCustomerDataAtom);
  const intl = useIntl();
  const API = useAPI();
  const notificationsRef = useRef({}); //os notifications keyed by contact id (entityId)

  const goToCustomerPage = (call) => {
    //go to customer page and passes the call data as extra-params
    const branchId = call?.assignedBranchId ?? selectedBranch?.id;
    if (call?.baseEntityId && branchId) {
      API.answerCall(
        call.baseEntityId,
        branchId,
        () => {
          setSelectedCustomerId(call.baseEntityId);
          setAdditionalCustomerData({
            callData: { ...call },
            customerName: call.baseEntityFullName,
            outcomesFilter: 'CUSTOMERS',
            branchId: call?.assignedBranchId ?? undefined,
          });
        },
        () => {}
      );
    }
  };

  const closeNotification = (entityId) => {
    notificationsRef.current[entityId]?.close();
    delete notificationsRef.current[entityId];
  };

  const removeCall = (call) => {
    setCalls((prev) => prev.filter((c) => c.id !== call.id));
    closeNotification(call.entityId);
  };

  const showNotification = (currentData) => {
    if (isNotificationSupported() && !visibilityState) {
      notify(
        {
          title: intl.messages.INBOUND_CALL,
          icon: '/favicon.ico',
          message: currentData?.entityFullName ?? intl.messages.UNAVAILABLE,
          vibrate: true,
          requireInteraction: true,
        },
        () => goToCustomerPage(currentData)
      )
        .then((n) => {
          if (n) notificationsRef.current[currentData.entityId] = n;
        })
        .catch((error) => {
          console.error('Failed to show incoming call notification', error);
        });
    }
  };

  useEffect(() => {
    //a new incoming call arrives: stack it next to the others (ignore duplicates)
    if (notifyIncomingCall?.id) {
      setCalls((prev) =>
        prev.some((c) => c.id === notifyIncomingCall.id)
          ? prev
          : [...prev, notifyIncomingCall]
      );
      showNotification(notifyIncomingCall);
    }
  }, [notifyIncomingCall]);

  useEffect(() => {
    //close the popup(s) of the given contact when the backend asks to
    if (notifyClosePopUp?.customerId) {
      const { customerId, answeredByUserId } = notifyClosePopUp;
      //keep the popup only for the operator who answered (call ended -> answeredByUserId is null)
      const answeredByMe =
        answeredByUserId &&
        userData?.id &&
        answeredByUserId.toLowerCase() === userData.id.toLowerCase();
      if (!answeredByMe) {
        setCalls((prev) => prev.filter((c) => c.entityId !== customerId));
        closeNotification(customerId);
      }
      setNotifyClosePopUp(null);
    }
  }, [notifyClosePopUp]);

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: '24px',
        right: '8px',
        zIndex: (theme) => theme.zIndex.snackbar,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        maxHeight: '100vh',
        overflowY: 'auto',
        pointerEvents: 'none', //let the cards receive clicks, not the container
      }}
    >
      {calls.map((call) => (
        <Slide
          key={call.id}
          direction="left"
          in
          appear
          mountOnEnter
          unmountOnExit
          timeout={800}
        >
          <Paper
            elevation={6}
            sx={{
              pointerEvents: 'auto',
              backgroundColor: '#1D5B9D',
              borderRadius: '10px',
              color: '#fff',
              p: '6px 16px 6px 8px',
            }}
          >
            <IncomingCallContent
              customerData={call}
              onClose={() => removeCall(call)}
              onClick={() => goToCustomerPage(call)}
            />
          </Paper>
        </Slide>
      ))}
    </Box>
  );
};

export default IncomingCallPopup;
